import React from 'react';
import { Button, Form, Input } from 'antd-mobile';
import {
  LockOutlined,
  MobileOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LocaleSwitcher } from '../../../components/LocaleSwitcher';
import { LoginFormValues } from './types';
import { loginPageStyles } from './styles';

interface LoginViewProps {
  loading: boolean;
  onSubmit: (values: LoginFormValues) => Promise<void>;
}

export const LoginView: React.FC<LoginViewProps> = ({ loading, onSubmit }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');

  return (
    <div style={loginPageStyles.page}>
      <div style={loginPageStyles.glowPrimary} />
      <div style={loginPageStyles.glowSecondary} />
      <div style={loginPageStyles.shell}>
        <div style={loginPageStyles.localeRow}>
          <LocaleSwitcher tone="light" labelMode="short" />
        </div>
        <div style={loginPageStyles.hero}>
          <div style={loginPageStyles.title}>{t('login.signInTitle')}</div>
          <div style={loginPageStyles.subtitle}>
            {t('login.subtitle')}
          </div>

          <div style={loginPageStyles.heroCard}>
            <div style={loginPageStyles.heroHeader}>
              <div style={loginPageStyles.heroDots}>
                <div style={loginPageStyles.heroDot} />
                <div style={loginPageStyles.heroDot} />
                <div style={loginPageStyles.heroDot} />
              </div>
              <div style={loginPageStyles.heroCardTitle}>{t('login.cardTitle')}</div>
            </div>

            <div style={loginPageStyles.featureRow}>
              <div style={loginPageStyles.featureItem}>
                <div style={loginPageStyles.featureValue}>{t('login.feature1Value')}</div>
                <div style={loginPageStyles.featureLabel}>{t('login.feature1Label')}</div>
              </div>
              <div style={loginPageStyles.featureItem}>
                <div style={loginPageStyles.featureValue}>{t('login.feature2Value')}</div>
                <div style={loginPageStyles.featureLabel}>{t('login.feature2Label')}</div>
              </div>
              <div style={loginPageStyles.featureItem}>
                <div style={loginPageStyles.featureValue}>{t('login.feature3Value')}</div>
                <div style={loginPageStyles.featureLabel}>{t('login.feature3Label')}</div>
              </div>
            </div>

            <div style={loginPageStyles.hintText}>
              {t('login.hint')}
            </div>
          </div>
        </div>

        <div style={loginPageStyles.formCard}>
          <div style={loginPageStyles.sectionTitle}>{t('login.welcomeBack')}</div>
          <div style={loginPageStyles.sectionSubtitle}>{t('login.sectionSubtitle')}</div>
          <Form
            layout="vertical"
            onFinish={async (values) => {
              await onSubmit(values as LoginFormValues);
            }}
            footer={
              <Button
                block
                type="submit"
                color="primary"
                loading={loading}
                style={loginPageStyles.submitButton}
              >
                {t('login.submit')}
              </Button>
            }
          >
            <Form.Item name="username" rules={[{ required: true, message: t('login.usernameRequired') }]}>
              <div style={loginPageStyles.inputShell}>
                <div style={loginPageStyles.inputIcon}>
                  <MobileOutlined />
                </div>
                <Input placeholder={t('login.usernamePlaceholder')} clearable style={loginPageStyles.input} />
              </div>
            </Form.Item>
            <Form.Item name="password" rules={[{ required: true, message: t('login.passwordRequired') }]}>
              <div style={loginPageStyles.inputShell}>
                <div style={loginPageStyles.inputIcon}>
                  <LockOutlined />
                </div>
                <Input placeholder={t('login.passwordPlaceholder')} type="password" clearable style={loginPageStyles.input} />
              </div>
            </Form.Item>
            <div style={loginPageStyles.assistRow}>
              <div style={loginPageStyles.assistBadge}>
                <SafetyCertificateOutlined style={{ marginRight: 6 }} />
                {t('login.secureAccess')}
              </div>
              <div style={loginPageStyles.assistLink}>{t('login.forgotPassword')}</div>
            </div>
          </Form>
        </div>

        <div style={loginPageStyles.footer}>
          {t('login.noAccount')}
          <span onClick={() => navigate('/register')} style={loginPageStyles.footerLink}>
            {t('login.goRegister')}
          </span>
        </div>
      </div>
    </div>
  );
};
